import { useState, useEffect, useCallback } from "react";
import { useFormBuilder } from "../hooks/useFormBuilder";
import { usePlanState } from "../hooks/usePlanState";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const SENTIMENT_CONFIG = {
    positive: { label: "Positive", bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-700 dark:text-green-400", dot: "bg-green-500" },
    neutral:  { label: "Neutral",  bg: "bg-gray-100 dark:bg-gray-700",      text: "text-gray-600 dark:text-gray-300",   dot: "bg-gray-400" },
    negative: { label: "Negative", bg: "bg-red-100 dark:bg-red-900/30",     text: "text-red-700 dark:text-red-400",     dot: "bg-red-500" },
    mixed:    { label: "Mixed",    bg: "bg-yellow-100 dark:bg-yellow-900/30", text: "text-yellow-700 dark:text-yellow-400", dot: "bg-yellow-500" },
};

const INTENT_LABELS = {
    buy: "Buy", get_info: "Info", complain: "Complaint", apply: "Apply", other: "Other", unclear: "—"
};

function SentimentChip({ sentiment }) {
    if (!sentiment) return null;
    const cfg = SENTIMENT_CONFIG[sentiment] || SENTIMENT_CONFIG.neutral;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
            {cfg.label}
        </span>
    );
}

function LeadScoreBadge({ score }) {
    if (score === null || score === undefined) return null;
    const color = score >= 80 ? "bg-green-500" : score >= 50 ? "bg-yellow-500" : "bg-gray-400";
    return (
        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-white text-xs font-bold ${color}`}>
            {score}
        </span>
    );
}

function AnalysisStatusBadge({ status }) {
    if (!status || status === "complete") return null;
    const map = {
        pending: { label: "Pending", cls: "text-gray-400 dark:text-gray-500" },
        processing: { label: "Analyzing…", cls: "text-blue-400 animate-pulse" },
        failed: { label: "Failed", cls: "text-red-400" },
    };
    const cfg = map[status] || map.pending;
    return <span className={`text-xs font-medium ${cfg.cls}`}>{cfg.label}</span>;
}

function FilterBar({ filters, onChange }) {
    return (
        <div className="flex flex-wrap gap-2 mb-4 p-3 bg-gray-50 dark:bg-[#23263a] border-[1px] dark:border-gray-700 rounded-lg">
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 self-center mr-1">Filter:</span>

            <select
                value={filters.sentiment}
                onChange={e => onChange({ ...filters, sentiment: e.target.value })}
                className="text-xs border-[1px] dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-[#181d33] dark:text-white"
            >
                <option value="">All Sentiment</option>
                <option value="positive">Positive</option>
                <option value="neutral">Neutral</option>
                <option value="negative">Negative</option>
                <option value="mixed">Mixed</option>
            </select>

            <select
                value={filters.intent}
                onChange={e => onChange({ ...filters, intent: e.target.value })}
                className="text-xs border-[1px] dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-[#181d33] dark:text-white"
            >
                <option value="">All Intent</option>
                <option value="buy">Buy</option>
                <option value="get_info">Get Info</option>
                <option value="complain">Complaint</option>
                <option value="apply">Apply</option>
            </select>

            <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500 dark:text-gray-400">Lead</span>
                <input
                    type="number" min="0" max="100" placeholder="Min"
                    value={filters.leadScoreMin}
                    onChange={e => onChange({ ...filters, leadScoreMin: e.target.value })}
                    className="text-xs border-[1px] dark:border-gray-600 rounded px-2 py-1 w-14 bg-white dark:bg-[#181d33] dark:text-white"
                />
                <span className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500">–</span>
                <input
                    type="number" min="0" max="100" placeholder="Max"
                    value={filters.leadScoreMax}
                    onChange={e => onChange({ ...filters, leadScoreMax: e.target.value })}
                    className="text-xs border-[1px] dark:border-gray-600 rounded px-2 py-1 w-14 bg-white dark:bg-[#181d33] dark:text-white"
                />
            </div>

            {(filters.sentiment || filters.intent || filters.leadScoreMin || filters.leadScoreMax) && (
                <button
                    onClick={() => onChange({ sentiment: "", intent: "", leadScoreMin: "", leadScoreMax: "" })}
                    className="text-xs text-indigo-500 hover:underline"
                >
                    Clear
                </button>
            )}
        </div>
    );
}

function FormResponses() {
    const { formId } = useParams();
    const navigate = useNavigate();
    const { getForm, getResponses, deleteResponse, reanalyzeSubmission, loading, error } = useFormBuilder();

    const [form, setForm] = useState(null);
    const [responses, setResponses] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [expandedRow, setExpandedRow] = useState(null);
    const [filters, setFilters] = useState({ sentiment: "", intent: "", leadScoreMin: "", leadScoreMax: "" });
    const { effectivePlan } = usePlanState();

    // AI response analysis is a response_intelligence_advanced feature (Pro+ / matching LTD tiers).
    const hasAiAnalysis = ["pro", "business", "agency"].includes(effectivePlan);

    const loadData = useCallback(async () => {
        const formData = await getForm(formId);
        if (formData) setForm(formData);

        const resData = await getResponses(formId, page, filters);
        if (resData) {
            setResponses(resData.responses);
            setTotal(resData.total);
            setTotalPages(resData.totalPages);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formId, page, filters]);

    useEffect(() => { loadData(); }, [loadData]);

    const handleDelete = async (responseId) => {
        if (!window.confirm("Delete this response?")) return;
        const result = await deleteResponse(formId, responseId);
        if (result) {
            setResponses(responses.filter((r) => r._id !== responseId));
            setTotal(total - 1);
            toast("Response deleted");
        }
    };

    const handleReanalyze = async (e, submissionId) => {
        e.stopPropagation();
        const result = await reanalyzeSubmission(submissionId);
        if (result) {
            toast("Re-analysis queued");
            setResponses(prev => prev.map(r =>
                r._id === submissionId
                    ? { ...r, analysis: { ...(r.analysis || {}), status: "pending" } }
                    : r
            ));
        }
    };

    const exportCSV = () => {
        if (!form || responses.length === 0) return;
        const cols = form.fields.map((f) => f.name);
        const headers = form.fields.map((f) => f.label);

        const escapeCSV = (val) => {
            const str = String(val || "");
            if (str.includes(",") || str.includes('"') || str.includes("\n")) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        };

        let csv = [...headers, "Submitted", "AI Summary", "Sentiment", "Lead Score", "Intent"].map(escapeCSV).join(",") + "\n";

        responses.forEach((resp) => {
            const row = cols.map((col) => escapeCSV(
                resp.responseData?.[col] || resp.responseData?.[form.fields.find((f) => f.name === col)?.label] || ""
            ));
            row.push(escapeCSV(resp.submittedAt ? new Date(resp.submittedAt).toLocaleString() : ""));
            row.push(escapeCSV(resp.analysis?.summary || ""));
            row.push(escapeCSV(resp.analysis?.sentiment || ""));
            row.push(escapeCSV(resp.analysis?.leadScore ?? ""));
            row.push(escapeCSV(resp.analysis?.intent || ""));
            csv += row.join(",") + "\n";
        });

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${form.title || "form"}-responses.csv`;
        link.click();
        URL.revokeObjectURL(url);
        toast("CSV exported!");
    };

    const columns = form?.fields?.map((f) => ({ name: f.name, label: f.label })) || [];

    return (
        <div className="min-h-screen max-w-7xl mx-auto px-4 sm:px-6 py-8 font-Rajdhani">
            {/* Header */}
            <div className="flex flex-wrap justify-between items-center mt-5 mb-4">
                <div>
                    <button
                        className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 mb-1"
                        onClick={() => navigate("/form-builder")}
                    >
                        &larr; Back to Forms
                    </button>
                    <h1 className="text-2xl font-bold">
                        Responses{form ? `: ${form.title}` : ""}
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 font-medium">{total} total responses</p>
                </div>
                <div className="flex gap-2 items-center">
                    <Link
                        to={`/form-builder/${formId}/insights`}
                        className="px-4 py-2 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium no-underline"
                    >
                        Insights
                    </Link>
                    {responses.length > 0 && (
                        <button
                            onClick={exportCSV}
                            className="px-4 py-2 text-sm border-[1px] dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-[#2c2f44] font-medium"
                        >
                            Export CSV
                        </button>
                    )}
                </div>
            </div>

            {/* Filter bar — analysis filters only relevant for paid tiers */}
            {hasAiAnalysis && (
                <FilterBar
                    filters={filters}
                    onChange={(f) => { setFilters(f); setPage(1); }}
                />
            )}

            {loading ? (
                <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="border-[1px] dark:border-gray-700 rounded-lg p-4 animate-pulse">
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2" />
                            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
                        </div>
                    ))}
                </div>
            ) : responses.length === 0 ? (
                <div className="text-center py-20 border-[1px] dark:border-gray-700 rounded-lg bg-white dark:bg-[#23263a]">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                    </div>
                    <p className="text-xl text-gray-500 dark:text-gray-400 mb-2">No responses yet</p>
                    <p className="text-gray-400 dark:text-gray-500 font-medium">Share your form to start collecting responses.</p>
                </div>
            ) : (
                <>
                    <div className="overflow-x-auto border-[1px] dark:border-gray-700 rounded-lg">
                        <table className="w-full text-sm font-normal">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-[#23263a] text-left">
                                    <th className="p-3 border-b dark:border-gray-700 font-semibold">#</th>
                                    {columns.map((col) => (
                                        <th key={col.name} className="p-3 border-b dark:border-gray-700 font-semibold">{col.label}</th>
                                    ))}
                                    <th className="p-3 border-b dark:border-gray-700 font-semibold">Submitted</th>
                                    {hasAiAnalysis && (
                                        <th className="p-3 border-b dark:border-gray-700 font-semibold min-w-[260px]">AI Analysis</th>
                                    )}
                                    <th className="p-3 border-b dark:border-gray-700 font-semibold">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {responses.map((resp, idx) => {
                                    const analysis = resp.analysis;
                                    const isExpanded = expandedRow === resp._id;
                                    return (
                                        <>
                                            <tr
                                                key={resp._id}
                                                className={`border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#23263a] cursor-pointer ${idx % 2 === 1 ? "bg-gray-25 dark:bg-[#1e2035]" : ""}`}
                                                onClick={() => setExpandedRow(isExpanded ? null : resp._id)}
                                            >
                                                <td className="p-3">{(page - 1) * 20 + idx + 1}</td>
                                                {columns.map((col) => {
                                                    const val = resp.responseData?.[col.name] || resp.responseData?.[col.label] || "-";
                                                    return (
                                                        <td key={col.name} className={`p-3 ${isExpanded ? "whitespace-pre-wrap" : "max-w-[180px] truncate"}`}>
                                                            {val}
                                                        </td>
                                                    );
                                                })}
                                                <td className="p-3 text-gray-500 dark:text-gray-400">
                                                    {resp.submittedAt ? new Date(resp.submittedAt).toLocaleDateString() : "-"}
                                                </td>
                                                {/* AI Analysis cell — paid tiers only */}
                                                {hasAiAnalysis && (
                                                    <td className="p-3" onClick={e => e.stopPropagation()}>
                                                        {!analysis ? (
                                                            <span className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500">—</span>
                                                        ) : analysis.status !== "complete" ? (
                                                            <AnalysisStatusBadge status={analysis.status} />
                                                        ) : (
                                                            <div className="space-y-1.5">
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <SentimentChip sentiment={analysis.sentiment} />
                                                                    {analysis.leadScore > 0 && (
                                                                        <LeadScoreBadge score={analysis.leadScore} />
                                                                    )}
                                                                    {analysis.intent && analysis.intent !== "unclear" && (
                                                                        <span className="text-xs px-2 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-medium">
                                                                            {INTENT_LABELS[analysis.intent] || analysis.intent}
                                                                        </span>
                                                                    )}
                                                                    {analysis.spamFlag && (
                                                                        <span className="text-xs px-2 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 font-medium">Spam</span>
                                                                    )}
                                                                </div>
                                                                {analysis.themes?.length > 0 && (
                                                                    <div className="flex flex-wrap gap-1">
                                                                        {analysis.themes.slice(0, 2).map((t, i) => (
                                                                            <span key={i} className="text-xs px-1.5 py-0.5 rounded-full border-[1px] dark:border-gray-600 text-gray-600 dark:text-gray-300">{t}</span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {resp.tags?.length > 0 && (
                                                                    <div className="flex flex-wrap gap-1">
                                                                        {resp.tags.slice(0, 3).map((t, i) => (
                                                                            <span key={i} className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${t.tag === "hot-lead" ? "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400" : "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400"}`}>
                                                                                #{t.tag}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                )}
                                                <td className="p-3">
                                                    <div className="flex flex-col gap-1">
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleDelete(resp._id); }}
                                                            className="text-red-500 hover:underline text-xs"
                                                        >
                                                            Delete
                                                        </button>
                                                        {hasAiAnalysis && analysis?.status === "complete" && (
                                                            <button
                                                                onClick={(e) => handleReanalyze(e, resp._id)}
                                                                className="text-indigo-500 hover:underline text-xs"
                                                            >
                                                                Re-analyze
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>

                                            {/* Expanded AI summary row — paid tiers only */}
                                            {hasAiAnalysis && isExpanded && analysis?.status === "complete" && (
                                                <tr key={`${resp._id}-expanded`} className="border-b dark:border-gray-700 bg-indigo-50 dark:bg-indigo-900/10">
                                                    <td colSpan={columns.length + (hasAiAnalysis ? 4 : 3)} className="px-4 py-3">
                                                        <div className="space-y-2 text-sm">
                                                            {analysis.summary && (
                                                                <div>
                                                                    <span className="font-semibold text-gray-700 dark:text-gray-300">AI Summary: </span>
                                                                    <span className="text-gray-600 dark:text-gray-400">{analysis.summary}</span>
                                                                </div>
                                                            )}
                                                            {analysis.leadRationale && (
                                                                <div>
                                                                    <span className="font-semibold text-gray-700 dark:text-gray-300">Lead Rationale: </span>
                                                                    <span className="text-gray-600 dark:text-gray-400">{analysis.leadRationale}</span>
                                                                </div>
                                                            )}
                                                            {analysis.themes?.length > 0 && (
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <span className="font-semibold text-gray-700 dark:text-gray-300">Themes:</span>
                                                                    {analysis.themes.map((t, i) => (
                                                                        <span key={i} className="text-xs px-2 py-0.5 rounded-full border-[1px] dark:border-gray-600 text-gray-600 dark:text-gray-300">{t}</span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
                                                                {analysis.qualityScore > 0 && <span>Quality: {analysis.qualityScore}/100</span>}
                                                                {analysis.language && <span>Language: {analysis.language}</span>}
                                                                {Object.keys(analysis.piiDetected || {}).length > 0 && (
                                                                    <span className="text-amber-600 dark:text-amber-400">PII: {Object.keys(analysis.piiDetected).join(", ")}</span>
                                                                )}
                                                                <span>Model: {analysis.modelVersion}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex justify-center gap-2 mt-4">
                            <button onClick={() => setPage(page - 1)} disabled={page <= 1} className="px-3 py-1 border-[1px] dark:border-gray-600 rounded disabled:opacity-30">
                                Previous
                            </button>
                            <span className="px-3 py-1 font-medium">Page {page} of {totalPages}</span>
                            <button onClick={() => setPage(page + 1)} disabled={page >= totalPages} className="px-3 py-1 border-[1px] dark:border-gray-600 rounded disabled:opacity-30">
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}

            {error && <p className="mt-3 text-red-600 dark:text-red-400 font-medium">{error}</p>}

            <ToastContainer
                position="top-right" autoClose={2000}
                theme={localStorage.getItem("theme") === "dark" ? "dark" : "light"}
            />
        </div>
    );
}

export default FormResponses;
